import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Modal,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsLineTooltip } from "recharts";
import axios from "axios";
import MediaViewer from "../../components/mediaviewer";
import * as XLSX from "xlsx"; // <-- Import xlsx library

// API endpoints
const API_VENDORS =
    "https://figazuresql.swancreate.com/api/swa/FINANCE_Vendors";
const API_EXPENSES =
    "https://figazuresql.swancreate.com/api/swa/FINANCE_Expenses";

const BlanketPOUsagePage = () => {
    const [vendors, setVendors] = useState([]);
    const [selectedVendorId, setSelectedVendorId] = useState("");
    const [vendorExpenses, setVendorExpenses] = useState([]);
    const [vendorBudget, setVendorBudget] = useState(0);
    const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
    const [mediaImageUrl, setMediaImageUrl] = useState("");

    // Fetch vendors on mount
    useEffect(() => {
        fetchVendors();
    }, []);

    const fetchVendors = async () => {
        try {
            const res = await axios.get(API_VENDORS);
            // Only vendors with a BudgetAmount value (non-null and > 0)
            const filteredVendors = res.data.filter(
                (vendor) =>
                    vendor.BudgetAmount !== null && Number(vendor.BudgetAmount) > 0
            );
            setVendors(filteredVendors);
            if (filteredVendors.length > 0) {
                // Auto-select the first vendor if none selected
                setSelectedVendorId(filteredVendors[0].VendorID);
                setVendorBudget(filteredVendors[0].BudgetAmount);
            }
        } catch (err) {
            console.error("Error fetching vendors:", err);
        }
    };

    // When selected vendor changes, fetch expense data for that vendor
    useEffect(() => {
        if (selectedVendorId) {
            const vendor = vendors.find(
                (v) => String(v.VendorID) === String(selectedVendorId)
            );
            if (vendor) {
                setVendorBudget(Number(vendor.BudgetAmount));
            }
            fetchVendorExpenses(selectedVendorId);
        }
    }, [selectedVendorId, vendors]);

    const fetchVendorExpenses = async (vendorID) => {
        try {
            const res = await axios.get(API_EXPENSES);
            // Filter expenses for the selected vendor (matching VendorID)
            const filteredExpenses = res.data.filter(
                (expense) => String(expense.VendorID) === String(vendorID)
            );
            setVendorExpenses(filteredExpenses);
        } catch (err) {
            console.error("Error fetching expenses:", err);
        }
    };

    // Sum total expenses for the selected vendor
    const totalExpenses = vendorExpenses.reduce(
        (sum, expense) => sum + (Number(expense.Amount) || 0),
        0
    );

    // Prepare data for the Pie Chart.
    // "Expenses" (red) = total expenses, "Remaining" (green) = (Budget - Expenses), not below zero.
    const pieData = [
        { name: "Expenses", value: totalExpenses },
        {
            name: "Remaining",
            value: Math.max(Number(vendorBudget) - totalExpenses, 0),
        },
    ];
    // Colors: red for expenses, green for remaining (softer shades)
    const pieColors = ["#FF6961", "#77DD77"];

    // New helper function: calculate monthly expense totals for the entire school year
    // (Fiscal months: July through December, then January through June)
    const calculateMonthlyExpenseTrend = () => {
        const fiscalMonths = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];
        return fiscalMonths.map((month) => {
            const monthExpenses = vendorExpenses
                .filter((expense) => {
                    if (!expense.ExpenseDate) return false;
                    const expenseMonth = new Date(expense.ExpenseDate).getMonth() + 1;
                    return expenseMonth === month;
                })
                .reduce((sum, expense) => sum + Number(expense.Amount || 0), 0);
            const monthName = new Date(0, month - 1).toLocaleString("default", {
                month: "short",
            });
            return { month: monthName, amount: monthExpenses };
        });
    };

    const monthlyExpenseTrend = calculateMonthlyExpenseTrend();

    // DataGrid columns for expense table
    const columns = [
        {
            field: "viewInvoice",
            headerName: "View Invoice",
            width: 150,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    sx={{ backgroundColor: "#32CD32", color: "#fff" }}
                    onClick={() => {
                        setMediaImageUrl(params.row.imageURL);
                        setMediaViewerOpen(true);
                    }}
                >
                    View Invoice
                </Button>
            ),
        },
        {
            field: "InvoiceNumber",
            headerName: "Invoice Number",
            width: 150,
        },
        {
            field: "ExpenseDate",
            headerName: "Invoice Date",
            width: 150,
            valueGetter: (params) =>
                params.row.ExpenseDate
                    ? new Date(params.row.ExpenseDate).toLocaleDateString()
                    : "",
        },
        {
            field: "Amount",
            headerName: "Amount",
            width: 150,
            valueFormatter: (params) =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(params.value || 0),
        },
        { field: "ExpenseCategory", headerName: "Category", width: 150 },
        {
            field: "ExpenseSubcategory",
            headerName: "Subcategory",
            width: 150,
            valueGetter: (params) => params.row.ExpenseSubcategory || "N/A",
        },
        { field: "Description", headerName: "Description", width: 250 },
        {
            field: "BlanketPONumber",
            headerName: "Blanket PO Number",
            width: 150,
            valueGetter: () => {
                const vendor = vendors.find((v) => v.VendorID === selectedVendorId);
                return vendor ? vendor.BlanketPONumber : "";
            },
        },
        // New Columns on the right side of the table
        {
            field: "isPaid",
            headerName: "Paid",
            width: 100,
            valueFormatter: (params) => (params.value ? "Yes" : "No"),
        },
        {
            field: "PaymentType",
            headerName: "Payment Type",
            width: 150,
        },
        {
            field: "PaymentDate",
            headerName: "Payment Date",
            width: 150,
            valueGetter: (params) =>
                params.row.PaymentDate
                    ? new Date(params.row.PaymentDate).toLocaleDateString()
                    : "",
        },
        {
            field: "PaymentInformation",
            headerName: "Payment Information",
            width: 200,
        },
    ];

    // Handle Excel Download
    const handleDownloadExcel = () => {
        try {
            // Prepare header row
            const wsData = [
                [
                    "Invoice Number",
                    "Invoice Date",
                    "Amount",
                    "Expense Category",
                    "Expense Subcategory",
                    "Description",
                    "Blanket PO Number",
                    "Paid",
                    "Payment Type",
                    "Payment Date",
                    "Payment Information",
                ],
            ];

            // Convert each expense record to a row
            vendorExpenses.forEach((exp) => {
                const dateStr = exp.ExpenseDate
                    ? new Date(exp.ExpenseDate).toLocaleDateString()
                    : "";
                const amountStr = new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(exp.Amount || 0);

                // BlanketPONumber from selected vendor
                const vendor = vendors.find((v) => v.VendorID === selectedVendorId);
                const blanketPO = vendor ? vendor.BlanketPONumber : "";
                const paidStr = exp.isPaid ? "Yes" : "No";
                const paymentDateStr = exp.PaymentDate
                    ? new Date(exp.PaymentDate).toLocaleDateString()
                    : "";
                wsData.push([
                    exp.InvoiceNumber || "",
                    dateStr,
                    amountStr,
                    exp.ExpenseCategory || "",
                    exp.ExpenseSubcategory || "",
                    exp.Description || "",
                    blanketPO,
                    paidStr,
                    exp.PaymentType || "",
                    paymentDateStr,
                    exp.PaymentInformation || "",
                ]);
            });

            // Create a workbook and a worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(wsData);

            // Append worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, "Expenses");

            // Generate Excel file and download
            XLSX.writeFile(wb, "VendorExpenses.xlsx");
        } catch (err) {
            console.error("Error exporting to Excel:", err);
        }
    };

    return (
        <Box p={2}>
            <Typography variant="h4" gutterBottom>
                Vendor Blanket PO Usage
            </Typography>
            <Box display="flex" alignItems="center" mb={2}>
                <FormControl sx={{ minWidth: 250, mr: 2 }}>
                    <InputLabel>Select Vendor</InputLabel>
                    <Select
                        value={selectedVendorId}
                        label="Select Vendor"
                        onChange={(e) => setSelectedVendorId(e.target.value)}
                    >
                        {vendors.map((vendor) => (
                            <MenuItem key={vendor.VendorID} value={vendor.VendorID}>
                                {vendor.VendorName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    sx={{
                        backgroundColor: "#FFD700",
                        color: "#000",
                        fontWeight: "bold",
                    }}
                    onClick={handleDownloadExcel}
                >
                    <img
                        src="https://static.vecteezy.com/system/resources/previews/022/100/783/non_2x/microsoft-excel-logo-transparent-free-png.png"
                        alt="Excel Logo"
                        style={{ height: "20px", marginRight: "8px" }}
                    />
                    Download Excel
                </Button>
            </Box>

            {/* Combined Charts: Pie Chart and Line Chart side by side */}
            <Box display="flex" justifyContent="space-between" mb={4} mt={2}>
                <Box flex={1} mr={2} height={600}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={200}
                                label={({ name, value }) =>
                                    `${name}: ${new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                    }).format(value)}`
                                }
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                                ))}
                            </Pie>
                            <RechartsTooltip
                                formatter={(value) =>
                                    new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                    }).format(value)
                                }
                            />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>
                <Box flex={1} ml={2} height={600}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={monthlyExpenseTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                            <XAxis dataKey="month" stroke="#fff" />
                            <YAxis
                                stroke="#fff"
                                tickFormatter={(value) =>
                                    new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                    }).format(value)
                                }
                            />
                            <RechartsLineTooltip
                                formatter={(value) =>
                                    new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                    }).format(value)
                                }
                            />
                            <Legend />
                            <Line type="monotone" dataKey="amount" stroke="#FF4500" strokeWidth={3} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            {/* Expense Data Grid */}
            <Box
                mb={2}
                sx={{
                    height: 400,
                    "& .MuiDataGrid-root": {
                        color: "#fff",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                        backgroundColor: "rgba(255,255,255,0.1) !important",
                    },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: "rgba(255,255,255,0.2) !important",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        backgroundColor: "#0F3460",
                        color: "#fff",
                    },
                }}
            >
                <DataGrid
                    rows={vendorExpenses}
                    columns={columns}
                    getRowId={(row) => row.apiid}
                    pageSize={10}
                    rowsPerPageOptions={[10, 20, 50]}
                />
            </Box>

            {/* Total Expenses Footer */}
            <Box mt={2} sx={{ textAlign: "right", fontSize: "1.2rem", color: "#fff" }}>
                Total Expenses:{" "}
                {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(totalExpenses)}
            </Box>

            {mediaViewerOpen && (
                <MediaViewer
                    open={mediaViewerOpen}
                    src={mediaImageUrl}
                    onClose={() => setMediaViewerOpen(false)}
                    type="image"
                />
            )}
        </Box>
    );
};

export default BlanketPOUsagePage;
